import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Gamepad2, 
  Users, 
  Map, 
  Code, 
  BookOpen, 
  Shield, 
  Zap,
  Target,
  Heart,
  Award,
  Sparkles,
  Crown,
  Rocket,
  Download,
  Building2
} from 'lucide-react';
import SEO from '../components/SEO';
import BlurText from '../components/BlurText';
import ShinyText from '../components/ShinyText';

const Home: React.FC = () => {
  const [showScrollIcon, setShowScrollIcon] = useState(true);

  const handleAnimationComplete = () => {
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      // Scroll icon'u 100px aşağı kaydırıldığında gizle
      if (scrollY > 100) {
        setShowScrollIcon(false);
      } else {
        setShowScrollIcon(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const stats = [
    {
      icon: Users,
      number: '5+',
      label: 'Aktif Sunucular',
      description: '7/24 Çalışma',
      color: 'from-gray-600 to-gray-700',
      delay: 0.1
    },
    {
      icon: Map,
      number: '50+',
      label: 'Özel Market',
      description: 'Benzersiz Eşyalar',
      color: 'from-gray-500 to-gray-600',
      delay: 0.2
    },
    {
      icon: Code,
      number: '100+',
      label: 'Özel Scriptler',
      description: 'Gelişmiş Sistemler',
      color: 'from-gray-700 to-gray-800',
      delay: 0.3
    },
    {
      icon: Heart,
      number: '1000+',
      label: 'Aktif Oyuncular',
      description: 'Günlük Ziyaretçiler',
      color: 'from-gray-400 to-gray-500',
      delay: 0.4
    }
  ];

  const features = [
    {
      icon: Zap,
      title: 'Yüksek Performans',
      description: 'Optimize edilmiş sunucular ve scriptlerle kesintisiz oyun deneyimi',
      color: 'from-gray-600 to-gray-700',
      gradient: 'from-gray-600/20 to-gray-700/20'
    },
    {
      icon: Target,
      title: 'Özel İçerik',
      description: 'Benzersiz haritalar, scriptler ve oyun modları',
      color: 'from-gray-500 to-gray-600',
      gradient: 'from-gray-500/20 to-gray-600/20'
    },
    {
      icon: Award,
      title: 'Kalite Garantisi',
      description: 'Sürekli güncellemeler ve yeni özellikler',
      color: 'from-gray-700 to-gray-800',
      gradient: 'from-gray-700/20 to-gray-800/20'
    }
  ];

  const quickActions = [
    { path: '/servers', label: 'Sunucular', icon: Users, color: 'from-gray-600 to-gray-700', description: 'Sunucularımıza katılın' },
    { path: '/market', label: 'Market', icon: Map, color: 'from-gray-500 to-gray-600', description: 'Oyun ici market' },
    { path: '/sponsors', label: 'Sponsorlar', icon: Gamepad2, color: 'from-gray-700 to-gray-800', description: 'Sponsorlarımız' },
    { path: '/scripts', label: 'Scriptler', icon: Code, color: 'from-gray-400 to-gray-500', description: 'Gelişmiş scriptler' },
    { path: '/guide', label: 'Rehber', icon: BookOpen, color: 'from-gray-600 to-gray-700', description: 'Başlangıç rehberi' },
    { path: '/rules', label: 'Kurallar', icon: Shield, color: 'from-gray-500 to-gray-600', description: 'Sunucu kuralları' }
  ];

  return (
    <>
      <SEO 
        title="Aedon Community | En Yeni FiveM Topluluğu"
        description="Aedon Community - En yeni ve en büyük FiveM topluluğu. Sunucularımız, oyunlarımız, scriptlerimiz ve premium hizmetlerimizle unutulmaz anlar yaşayın. FiveM RP sunucuları, oyun scriptleri ve topluluk hizmetleri."
        keywords="Aedon, Aedon Community, Topluluk, FiveM, FiveM Topluluğu, FiveM RP, FiveM Sunucu, FiveM Script, FiveM Oyun, FiveM Sunucu, FiveM Roleplay, FiveM Topluluk, Aedon FiveM, Topluluk FiveM, FiveM Topluluk, FiveM Sunucu, FiveM RP"
        url="/"
      />
      <div className="min-h-screen bg-dark-900">
        {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-transparent to-primary-600/10"></div>
        
        {/* Enhanced Floating Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-primary-500/40 rounded-full"
              animate={{
                x: [0, 150, 0],
                y: [0, -150, 0],
                opacity: [0.2, 0.8, 0.2],
                scale: [1, 1.5, 1],
              }}
              transition={{
                duration: 10 + i * 3,
                repeat: Infinity,
                delay: i * 0.3,
              }}
              style={{
                left: `${10 + i * 8}%`,
                top: `${20 + i * 6}%`,
              }}
            />
          ))}
        </div>

        {/* Background Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(59, 130, 246, 0.3) 1px, transparent 0)`,
            backgroundSize: '50px 50px'
          }}></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="inline-flex items-center px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-medium mb-8"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              <ShinyText text="Aedon Toplulugu" speed={5} className="text-primary-400" />
            </motion.div>

            {/* Main Title */}
            <div className="relative text-6xl md:text-7xl lg:text-8xl font-gaming mb-8">
              <BlurText
                text="Aedon Community"
                delay={150}
                animateBy="words"
                direction="top"
                onAnimationComplete={handleAnimationComplete}
                className="text-6xl md:text-7xl lg:text-8xl font-gaming"
              />
              <motion.div
                className="absolute top-0 right-0 -translate-y-2 translate-x-4"
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Crown className="w-8 h-8 text-yellow-400" />
              </motion.div>
            </div>

            {/* Subtitle */}
            <motion.p 
              className="text-xl md:text-2xl lg:text-3xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
            >
              Aedon topluluğuna hoş geldiniz.   
              <br />
              <span className="text-primary-400 font-semibold">Unutulmaz anlar yaşayın</span> sunucularımız, oyunlarımız ve scriptlerimizle.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6 }}
            >
              <Link 
                to="/servers" 
                className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 rounded-full overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-primary-500/25 transform hover:-translate-y-1"
              >
                <span className="relative z-10 flex items-center">
                  <Rocket className="w-5 h-5 mr-3 group-hover:animate-bounce" />
                  Sunuculara Katıl
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-primary-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-primary-500 opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-xl"></div>
              </Link>
              
              <Link 
                to="/guide" 
                className="group relative inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-dark-700/50 backdrop-blur-sm border border-dark-600 rounded-full overflow-hidden transition-all duration-300 hover:scale-105 hover:bg-dark-600/50 transform hover:-translate-y-1"
              >
                <span className="relative z-10 flex items-center">
                  <BookOpen className="w-5 h-5 mr-3 group-hover:rotate-12 transition-transform" />
                  Rehberi Görüntüle
                </span>
              </Link>
            </motion.div>

            {/* Stats Preview */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex justify-center space-x-8 text-center"
            >
              {stats.slice(0, 3).map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl font-bold text-primary-400">{stat.number}</div>
                  <div className="text-sm text-gray-400">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Enhanced Scroll Indicator */}
        <motion.div 
          className="absolute bottom-11 left-1/2 transform -translate-x-1/2"
          animate={{ 
            y: [0, 10, 0],
            opacity: showScrollIcon ? 1 : 0
          }}
          transition={{ 
            y: { duration: 2, repeat: Infinity },
            opacity: { duration: 0.3, ease: "easeInOut" }
          }}
        >
          <div className="w-6 h-10 border-2 border-primary-500 rounded-full flex justify-center relative">
            <div className="w-1 h-3 bg-primary-500 rounded-full mt-2 animate-pulse"></div>
            <div className="absolute -bottom-8 text-xs text-primary-400 font-medium">Aşağı Kaydır</div>
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-dark-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-gaming font-bold text-white mb-4">
              Topluluk <ShinyText text="Istatistikleri" speed={3} className="inline" />
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Büyüyen topluluğumuzun güçlü sayıları
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: stat.delay }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="group relative"
                >
                  <div className="relative bg-dark-700/50 backdrop-blur-sm rounded-2xl p-8 border border-dark-600 hover:border-primary-500/50 transition-all duration-300 hover:shadow-2xl hover:shadow-primary-500/10">
                    <div className={`w-16 h-16 mx-auto mb-6 rounded-xl bg-gradient-to-r ${stat.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 group-hover:rotate-6`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-4xl font-bold text-white mb-2 group-hover:text-primary-400 transition-colors">
                      {stat.number}
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{stat.label}</h3>
                    <p className="text-gray-400 text-sm">{stat.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-20 bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-gaming font-bold text-white mb-4">
              Hızlı <ShinyText text="Erisim" speed={3} className="inline" />
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Topluluğumuzun tüm özelliklerine hızlıca erişin
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                >
                  <Link
                    to={action.path}
                    className="group relative block bg-dark-800/50 backdrop-blur-sm rounded-2xl p-6 border border-dark-700 hover:border-primary-500/50 transition-all duration-300 text-center hover:bg-dark-700/50"
                  >
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-xl bg-gradient-to-r ${action.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 group-hover:rotate-6`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-white font-semibold group-hover:text-primary-400 transition-colors mb-2">
                      {action.label}
                    </h3>
                    <p className="text-xs text-gray-400 group-hover:text-gray-300 transition-colors">
                      {action.description}
                    </p>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-dark-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-l from-primary-500/5 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-gaming font-bold text-white mb-4">
              Neden <ShinyText text="Bizi Seçmelisiniz?" speed={3} className="inline" />
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Topluluğumuzu özel kılan özellikler
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  whileHover={{ scale: 1.02, y: -5 }}
                  className="group relative"
                >
                  <div className={`relative bg-dark-700/50 backdrop-blur-sm rounded-2xl p-8 border border-dark-600 hover:border-primary-500/50 transition-all duration-300 h-full hover:shadow-2xl hover:shadow-primary-500/10`}>
                    <div className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                    <div className="relative z-10">
                      <div className={`w-20 h-20 mx-auto mb-6 rounded-xl bg-gradient-to-r ${feature.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 group-hover:rotate-6`}>
                        <Icon className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="text-2xl font-semibold text-white mb-4 group-hover:text-primary-400 transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-gray-400 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-500/10 to-primary-600/10 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 to-transparent"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
              <h2 className="text-4xl md:text-5xl font-gaming font-bold text-white mb-6">
                Hemen <ShinyText text="Baslayın" speed={3} className="inline" />
              </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Aedon topluluğumuza katılın ve unutulmaz oyun deneyimleri keşfedin
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/servers" 
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-primary-500 to-primary-600 rounded-full hover:scale-105 transition-all duration-300 transform hover:-translate-y-1"
              >
                <Rocket className="w-5 h-5 mr-3" />
                Sunuculara Katıl
              </Link>
              <Link 
                to="/guide" 
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-dark-700/50 backdrop-blur-sm border border-dark-600 rounded-full hover:scale-105 transition-all duration-300 transform hover:-translate-y-1"
              >
                <Download className="w-5 h-5 mr-3" />
                Rehberi İndir
              </Link>
              <Link 
                to="/sponsors/apply" 
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full hover:scale-105 transition-all duration-300 transform hover:-translate-y-1"
              >
                <Building2 className="w-5 h-5 mr-3" />
                Sponsor Ol
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Home; 